import React, { useState, useRef } from "react";
import useHandleUsers from "../hooks/useHandleUsers";
import useForm from "../../forms/hooks/useForm";
import { initialLogInForm } from "../helpers/initialForms/initialLoginForm";
import loginSchema from "../models/Joi/loginSchema";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import ROUTES from "../../router/routesModel";
import { intendedDestination } from "../../router/intendedDestination";
import { useUser } from "../providers/UserProvider";
import { isSupabaseConfigured } from "../../lib/supabaseClient";
import { useKeyboardInset } from "../../native/keyboardInset";
import { FEATURE_FLAGS } from "../../utils/featureFlags";
import { dvh } from "../../styles/viewportHeight";
import TradeloftMark from "../../trades/TradeloftMark";
import { SAFE_TOP } from "../../styles/safeArea";

// The beta recruits worldwide when roaming is on, and an international
// tester's first screen must not tell them the app is for another country.
// With roaming off the server only serves Israel, and the copy says so.
// Read at render, not at import, so the copy always matches the flag.
const inRegion = () => (FEATURE_FLAGS.ENABLE_ROAMING ? "" : " in Israel");

/**
 * The sign-in panel's resting bottom padding, in pixels — the third value of
 * its `padding` shorthand when no keyboard is open.
 */
const FORM_PAD_BOTTOM_PX = 60;

const FEATURES = [
  {
    tag: "MARKET PRICES",
    title: "Real Market Value",
    // Not "live": these are stored snapshots, shown with the date they were
    // last updated (#64). Claiming live data we do not have is the kind of
    // small lie that costs a beta its credibility — and so was "Cardmarket, in
    // euros" once most cards came to be priced by the provider in dollars
    // (tablet QA, versionCode 11). Same wording as Settings → Display currency.
    desc: "Every card carries its market value — from Cardmarket (€) or our price provider ($) — stamped with the date it was last updated, so you know what it's worth before you trade or list it.",
    img: "https://images.pokemontcg.io/base1/4.png",
    img2: "https://images.pokemontcg.io/base1/1.png",
    accent: "#f97316",
  },
  {
    tag: "NEARBY COLLECTORS",
    title: "Trade With Collectors Near You",
    desc: () => `Discover collectors near you${inRegion()}. The map shows you when someone nearby has a card from your wishlist — meet up, inspect, trade in person.`,
    img: "https://images.pokemontcg.io/neo1/2.png",
    img2: "https://images.pokemontcg.io/base2/10.png",
    accent: "#3b82f6",
  },
  {
    tag: "WISHLIST",
    title: "Save the Cards You Want",
    // Used to promise an instant alert when a nearby collector listed a match —
    // no alert exists for a new match (email covers trades and chat only, and
    // there is no push); matches appear when the map is opened (P0 area C).
    desc: "Build your wishlist and the map shows you which nearby collectors have a match. No global noise — just real signals from real collectors near you.",
    img: "https://images.pokemontcg.io/base1/2.png",
    img2: "https://images.pokemontcg.io/base1/3.png",
    accent: "#8b5cf6",
  },
];

const LoginPage = () => {
  const { user } = useUser();
  const { handleLogin, handleGoogleLogin } = useHandleUsers();
  const { value, handleInputChange, onSubmit } = useForm(
    initialLogInForm,
    loginSchema,
    handleLogin
  );
  const { data, errors } = value;
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  // Sign-up keeps where the reader was going (a shared trade list's offer, for
  // one), so a new account lands there too instead of on the map.
  const goToSignUp = () => {
    if (location.state?.from) navigate(ROUTES.SIGNUP, { state: { from: location.state.from } });
    else navigate(ROUTES.SIGNUP);
  };
  // The same for a reader who forgot their password on the way (JRN-1b).
  const goToForgotPassword = () => {
    if (location.state?.from) navigate(ROUTES.FORGOT_PASSWORD, { state: { from: location.state.from } });
    else navigate(ROUTES.FORGOT_PASSWORD);
  };
  // Android 15+ (API 36) draws edge-to-edge and no longer resizes the WebView
  // when the IME opens, so this page keeps its full height and the sign-in
  // panel — the last thing before the footer — sits behind the keyboard with
  // nothing underneath to scroll into. See src/native/keyboardInset.js.
  const keyboardInset = useKeyboardInset();

  if (user) {
    const from = intendedDestination(location, ROUTES.MAP);
    return <Navigate to={from} replace />;
  }

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSubmitting(true);
    await onSubmit(e);
    setSubmitting(false);
  };

  return (
    <div style={{ fontFamily: "Inter, sans-serif", background: "#0a0a0f", color: "#f1f5f9", overflowX: "hidden" }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes floatSlow {
          0%,100% { transform: translateY(0) rotate(-8deg); }
          50%      { transform: translateY(-16px) rotate(-8deg); }
        }
        @keyframes floatSlow2 {
          0%,100% { transform: translateY(0) rotate(6deg); }
          50%      { transform: translateY(-20px) rotate(6deg); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        input::placeholder { color: #374151; }
        input:-webkit-autofill {
          -webkit-box-shadow: 0 0 0 100px #111827 inset !important;
          -webkit-text-fill-color: #e2e8f0 !important;
        }
        .feature-img { border-radius: 16px; box-shadow: 0 16px 48px rgba(0,0,0,0.6); }
      `}</style>

      {/* ── NAV ── */}
      {/* Fixed at y=0 and edge to edge: without the inset the wordmark and
          "Sign In" sat under the status bar, and a tap on the top of Sign In
          went to the system instead (tablet QA, versionCode 11). */}
      <div data-testid="landing-nav" style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        padding: `calc(14px + ${SAFE_TOP}) 20px 14px`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "rgba(10,10,15,0.85)",
        backdropFilter: "blur(20px)",
        borderBottom: "1px solid rgba(255,255,255,0.05)",
      }}>
        <div style={{ color: "#fff" }}>
          <TradeloftMark size={30} wordmarkSize={18} />
        </div>
        <button onClick={scrollToForm} style={{
          background: "#f97316", color: "#fff", border: "none",
          borderRadius: 10, padding: "8px 18px", fontSize: 13,
          fontWeight: 800, cursor: "pointer", fontFamily: "Inter, sans-serif",
        }}>
          Sign In
        </button>
      </div>

      {/* ── HERO ── */}
      <div style={{
        minHeight: dvh(100),
        paddingTop: 60,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        position: "relative", overflow: "hidden",
        background: "radial-gradient(ellipse at 50% 60%, rgba(124,58,237,0.2) 0%, transparent 65%), linear-gradient(180deg, #0a0a0f 0%, #12071e 100%)",
        // + the inset the fixed nav above now carries, so the hero clears it.
        padding: `calc(80px + ${SAFE_TOP}) 20px 60px`,
      }}>
        {/* Floating card art */}
        <div style={{ position: "absolute", top: "18%", left: "5%", animation: "floatSlow 5s ease-in-out infinite", opacity: 0.55 }}>
          <img src="https://images.pokemontcg.io/base1/4.png" alt="" width={100} className="feature-img" style={{ transform: "rotate(-12deg)" }} />
        </div>
        <div style={{ position: "absolute", top: "22%", right: "4%", animation: "floatSlow2 6s ease-in-out infinite", opacity: 0.55 }}>
          <img src="https://images.pokemontcg.io/base1/1.png" alt="" width={110} className="feature-img" style={{ transform: "rotate(10deg)" }} />
        </div>
        <div style={{ position: "absolute", bottom: "15%", left: "8%", animation: "floatSlow2 4.5s ease-in-out 1s infinite", opacity: 0.4 }}>
          <img src="https://images.pokemontcg.io/neo1/2.png" alt="" width={80} className="feature-img" style={{ transform: "rotate(-6deg)" }} />
        </div>
        <div style={{ position: "absolute", bottom: "18%", right: "6%", animation: "floatSlow 5.5s ease-in-out 0.5s infinite", opacity: 0.4 }}>
          <img src="https://images.pokemontcg.io/base1/2.png" alt="" width={88} className="feature-img" style={{ transform: "rotate(14deg)" }} />
        </div>

        {/* Hero text */}
        <div style={{ textAlign: "center", position: "relative", zIndex: 1, animation: "fadeUp 0.8s ease both" }}>
          <div style={{
            display: "inline-block", background: "rgba(249,115,22,0.15)",
            border: "1px solid rgba(249,115,22,0.35)", borderRadius: 999,
            padding: "5px 14px", fontSize: 11, fontWeight: 800, color: "#f97316",
            textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 20,
          }}>
            {inRegion() ? `Pokémon TCG Trading${inRegion()}` : "Pokémon TCG Trading Near You"}
          </div>

          <h1 style={{
            margin: "0 0 20px", fontSize: "clamp(36px, 10vw, 52px)",
            fontWeight: 900, lineHeight: 1.08, letterSpacing: "-1.5px",
            background: "linear-gradient(180deg, #ffffff 0%, #c4b5fd 100%)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          }}>
            The Ultimate<br />Trading Card<br />Arena
          </h1>

          <p style={{
            margin: "0 auto 32px", maxWidth: 320,
            color: "#64748b", fontSize: 16, lineHeight: 1.6, fontWeight: 400,
          }}>
            Trade Pokémon TCG cards with collectors near you{inRegion()}. Real prices, real cards, real people.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "center" }}>
            <button onClick={scrollToForm} style={{
              background: "linear-gradient(135deg, #f97316, #ea580c)",
              color: "#fff", border: "none", borderRadius: 14,
              padding: "15px 40px", fontSize: 17, fontWeight: 900,
              cursor: "pointer", fontFamily: "Inter, sans-serif",
              boxShadow: "0 4px 24px rgba(249,115,22,0.45)",
              letterSpacing: "-0.2px",
            }}>
              Start Free →
            </button>
            <button onClick={() => goToSignUp()} style={{
              background: "none", border: "none", color: "#475569",
              fontSize: 14, fontWeight: 600, cursor: "pointer",
              fontFamily: "Inter, sans-serif",
              textDecoration: "underline", textDecorationColor: "rgba(71,85,105,0.4)",
            }}>
              Create an account
            </button>
          </div>
        </div>

        {/* Scroll indicator */}
        <div style={{ position: "absolute", bottom: 24, left: "50%", transform: "translateX(-50%)", opacity: 0.4 }}>
          <div style={{ width: 1, height: 40, background: "linear-gradient(to bottom, transparent, #7c3aed)", margin: "0 auto" }} />
        </div>
      </div>

      {/* ── FEATURES ── */}
      {FEATURES.map((f, i) => (
        <div key={i} style={{
          padding: "56px 20px",
          background: i % 2 === 0
            ? "linear-gradient(180deg, #0a0a0f 0%, #0d0b18 100%)"
            : "linear-gradient(180deg, #0d0b18 0%, #0a0a0f 100%)",
          borderTop: "1px solid rgba(255,255,255,0.04)",
        }}>
          {/* Tag */}
          <div style={{
            display: "inline-block",
            background: `${f.accent}18`,
            border: `1px solid ${f.accent}40`,
            borderRadius: 999, padding: "4px 12px",
            fontSize: 10, fontWeight: 800, color: f.accent,
            textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16,
          }}>
            {f.tag}
          </div>

          <h2 style={{
            margin: "0 0 12px", fontSize: 26, fontWeight: 900,
            letterSpacing: "-0.5px", color: "#f1f5f9", lineHeight: 1.2,
          }}>
            {f.title}
          </h2>
          <p style={{ margin: "0 0 28px", color: "#64748b", fontSize: 15, lineHeight: 1.7 }}>
            {typeof f.desc === "function" ? f.desc() : f.desc}
          </p>

          {/* Card art pair */}
          <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
            <div style={{
              flex: 1, borderRadius: 16, overflow: "hidden",
              background: "#111827",
              padding: 12,
              border: "1px solid rgba(255,255,255,0.06)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <img src={f.img} alt="" style={{ width: "100%", maxWidth: 140, borderRadius: 10, display: "block", margin: "0 auto" }} />
            </div>
            <div style={{
              flex: 1, borderRadius: 16, overflow: "hidden",
              background: "#111827",
              padding: 12, paddingTop: 28,
              border: "1px solid rgba(255,255,255,0.06)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <img src={f.img2} alt="" style={{ width: "100%", maxWidth: 140, borderRadius: 10, display: "block", margin: "0 auto" }} />
            </div>
          </div>
        </div>
      ))}

      {/* ── LOGIN FORM ── */}
      <div ref={formRef} style={{
        // The extra bottom padding is scroll room, not decoration: it lets the
        // WebView scroll the focused input and the "Enter the Arena" button up
        // out from under the keyboard instead of hitting the end of the
        // document. It sits behind the keyboard, so the panel's background
        // keeps running to the bottom of the screen. 0 on web.
        padding: `48px 20px ${FORM_PAD_BOTTOM_PX + keyboardInset}px`,
        background: "linear-gradient(180deg, #0a0a0f 0%, #0d0722 100%)",
        borderTop: "1px solid rgba(124,58,237,0.15)",
      }}>
        <div style={{
          fontSize: 10, fontWeight: 800, color: "#f97316",
          textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 12,
        }}>
          Ready to enter the Arena?
        </div>
        <h2 style={{
          margin: "0 0 8px", fontSize: 28, fontWeight: 900,
          color: "#fff", letterSpacing: "-0.5px", lineHeight: 1.15,
        }}>
          Sign in to your account
        </h2>
        <p style={{ margin: "0 0 28px", color: "#475569", fontSize: 14 }}>
          No account yet?{" "}
          <button onClick={() => goToSignUp()} style={{
            background: "none", border: "none", color: "#a78bfa",
            fontWeight: 700, cursor: "pointer", fontFamily: "Inter, sans-serif",
            fontSize: 14, padding: 0,
          }}>
            Create one for free →
          </button>
        </p>

        <form onSubmit={handleSubmit}>
          {/* Email. The label names the input and an error describes it: with
              neither, a screen reader read only the placeholder and never the
              error (E2E audit 17·9). */}
          <div style={{ marginBottom: 16 }}>
            <label htmlFor="login-email" style={{
              display: "block", fontSize: 11, fontWeight: 700, color: "#4b5563",
              textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 7,
            }}>Email</label>
            <input
              id="login-email"
              type="email" name="email"
              aria-invalid={errors.email ? true : undefined}
              aria-describedby={errors.email ? "login-email-error" : undefined}
              value={data.email || ""}
              onChange={handleInputChange}
              placeholder="you@example.com"
              autoComplete="email"
              style={{
                width: "100%", boxSizing: "border-box",
                background: "#111827",
                border: `1.5px solid ${errors.email ? "#f87171" : "rgba(255,255,255,0.08)"}`,
                borderRadius: 12, padding: "13px 14px",
                fontSize: 15, color: "#e2e8f0",
                fontFamily: "Inter, sans-serif", outline: "none",
                transition: "border-color 0.2s",
              }}
              onFocus={(e) => { e.target.style.borderColor = "#7c3aed"; }}
              onBlur={(e)  => { e.target.style.borderColor = errors.email ? "#f87171" : "rgba(255,255,255,0.08)"; }}
            />
            {errors.email && <p id="login-email-error" style={{ margin: "5px 0 0", fontSize: 12, color: "#f87171" }}>{typeof errors.email === "string" ? errors.email : errors.email?.message}</p>}
          </div>

          {/* Password */}
          <div style={{ marginBottom: 10 }}>
            <label htmlFor="login-password" style={{
              display: "block", fontSize: 11, fontWeight: 700, color: "#4b5563",
              textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 7,
            }}>Password</label>
            <div style={{ position: "relative" }}>
              <input
                id="login-password"
                type={showPassword ? "text" : "password"}
                name="password"
                aria-invalid={errors.password ? true : undefined}
                aria-describedby={errors.password ? "login-password-error" : undefined}
                value={data.password || ""}
                onChange={handleInputChange}
                placeholder="••••••••"
                autoComplete="current-password"
                style={{
                  width: "100%", boxSizing: "border-box",
                  background: "#111827",
                  border: `1.5px solid ${errors.password ? "#f87171" : "rgba(255,255,255,0.08)"}`,
                  borderRadius: 12, padding: "13px 44px 13px 14px",
                  fontSize: 15, color: "#e2e8f0",
                  fontFamily: "Inter, sans-serif", outline: "none",
                  transition: "border-color 0.2s",
                }}
                onFocus={(e) => { e.target.style.borderColor = "#7c3aed"; }}
                onBlur={(e)  => { e.target.style.borderColor = errors.password ? "#f87171" : "rgba(255,255,255,0.08)"; }}
              />
              <button type="button" aria-label={showPassword ? "Hide password" : "Show password"} onClick={() => setShowPassword(p => !p)} style={{
                position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
                background: "none", border: "none", cursor: "pointer", color: "#475569",
                padding: 4, display: "flex", alignItems: "center",
              }}>
                {showPassword ? (
                  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {errors.password && <p id="login-password-error" style={{ margin: "5px 0 0", fontSize: 12, color: "#f87171" }}>{typeof errors.password === "string" ? errors.password : errors.password?.message}</p>}
          </div>

          {/* Login refuses an unverified account with a 403, and the resend
              form used to sit ONLY on /verify-email/:token — a page reachable
              exclusively from the email that never arrived. This is the way
              in. Kept beside "Forgot password?" because it is the same kind of
              problem: the account exists and the person cannot get into it. */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              marginBottom: 24,
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              data-testid="login-resend-verification"
              onClick={() => navigate(ROUTES.VERIFY_EMAIL_HELP)}
              style={{
                background: "none", border: "none", color: "#9ca3af",
                fontSize: 13, fontWeight: 500, cursor: "pointer",
                fontFamily: "Inter, sans-serif", padding: 0, textAlign: "left",
              }}
            >
              Didn&apos;t get the verification email?
            </button>
            <button type="button" onClick={goToForgotPassword} style={{
              background: "none", border: "none", color: "#7c3aed",
              fontSize: 13, fontWeight: 600, cursor: "pointer",
              fontFamily: "Inter, sans-serif", padding: 0,
            }}>
              Forgot password?
            </button>
          </div>

          {isSupabaseConfigured() && (
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={submitting}
              style={{
                width: "100%",
                background: "#111827",
                color: "#e2e8f0",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 14,
                padding: "13px",
                fontSize: 15,
                fontWeight: 700,
                cursor: submitting ? "default" : "pointer",
                fontFamily: "Inter, sans-serif",
                marginBottom: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
                <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303C33.42 32.657 29.223 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
                <path fill="#FF3D00" d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
                <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
                <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
              </svg>
              Continue with Google
            </button>
          )}

          <button type="submit" disabled={submitting} style={{
            width: "100%",
            background: submitting ? "rgba(249,115,22,0.4)" : "linear-gradient(135deg,#f97316,#ea580c)",
            color: "#fff", border: "none", borderRadius: 14,
            padding: "15px", fontSize: 16, fontWeight: 900,
            cursor: submitting ? "default" : "pointer",
            fontFamily: "Inter, sans-serif",
            boxShadow: submitting ? "none" : "0 4px 24px rgba(249,115,22,0.45)",
            letterSpacing: "-0.2px",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            marginBottom: 14, transition: "all 0.2s",
          }}>
            {submitting ? (
              <>
                <div style={{
                  width: 18, height: 18,
                  border: "2.5px solid rgba(255,255,255,0.25)",
                  borderTopColor: "#fff", borderRadius: "50%",
                  animation: "spin 0.7s linear infinite",
                }} />
                Signing in…
              </>
            ) : "Enter the Arena →"}
          </button>

          <button type="button" onClick={() => goToSignUp()} style={{
            width: "100%", background: "transparent",
            color: "#6b7280", border: "1px solid rgba(255,255,255,0.07)",
            borderRadius: 14, padding: "13px", fontSize: 15,
            fontWeight: 700, cursor: "pointer",
            fontFamily: "Inter, sans-serif",
          }}>
            Create New Account
          </button>
        </form>
      </div>

      {/* ── FOOTER ── */}
      <div style={{
        padding: "28px 20px",
        borderTop: "1px solid rgba(255,255,255,0.05)",
        background: "#060609",
      }}>
        <div style={{ color: "#9ca3af", marginBottom: 16 }}>
          <TradeloftMark size={26} wordmarkSize={15} accent="#a78bfa" />
        </div>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 20 }}>
          {[
            // #65. Privacy/Terms now point at the in-app routes rather than an
            // external host that does not resolve (#26) and would leave the app
            // inside the Capacitor wrap. Support stays an external mailto.
            { label: "Privacy", to: ROUTES.PRIVACY },
            { label: "Terms", to: ROUTES.TERMS },
            { label: "Support", href: "mailto:support@tradeloft.app" },
          ].map(({ label, href, to }) =>
            to ? (
              <a
                key={label}
                href={to}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(to);
                }}
                style={{ fontSize: 13, color: "#374151", fontWeight: 500, textDecoration: "none" }}
              >
                {label}
              </a>
            ) : (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: "#374151", fontWeight: 500, textDecoration: "none" }}>{label}</a>
            )
          )}
        </div>
        <p style={{ margin: 0, fontSize: 12, color: "#1f2937" }}>
          © 2026 Tradeloft. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
